import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

interface FailureAnalysisData {
  analyzed_at: string;
  database: {
    total_failed_runs: number;
    with_results_json: number;
    without_results_json: number;
    error_categories: Record<string, number>;
    recent_failures: any[];
  };
  results_files: {
    runs_analyzed: number;
    task_failures: Record<string, number>;
    failure_points: Record<string, number>;
  };
  summary: {
    total_db_failures: number;
    hung_before_completion: number;
    most_common_error_category: [string, number] | null;
  };
  recommendations: string[];
}

const StatCard = styled(Card)(({ theme, color = 'primary' }) => {
  const paletteColor = theme.palette[color as keyof typeof theme.palette] as any;
  const borderColor = paletteColor?.main || theme.palette.primary.main;
  return {
    height: '100%',
    borderLeft: `4px solid ${borderColor}`,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  };
});

export default function ValidationFailureAnalysis() {
  const [data, setData] = useState<FailureAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/validation/stats/failure-analysis');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAnalysis();
  }, []);

  const handleExport = () => {
    if (!data) return;
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failure-analysis-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          🔍 Failure Analysis
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!data}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={runAnalysis}
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {data && (
        <>
          {/* Summary Stats */}
          <Grid container spacing={3} mb={3}>
            <Grid xs={12} sm={6} md={3}>
              <StatCard color="error">
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {data.database.total_failed_runs.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Failed Runs
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard color="error">
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {data.database.without_results_json.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hung (No Results)
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard color="warning">
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {data.database.with_results_json.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed w/ Errors
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard color="info">
                <CardContent>
                  <Typography variant="h6" fontWeight="bold">
                    {data.summary.most_common_error_category?.[0] || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Top Error Category ({data.summary.most_common_error_category?.[1] || 0})
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          {/* Recommendations */}
          {data.recommendations.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                💡 Recommendations
              </Typography>
              {data.recommendations.map((rec, idx) => (
                <Alert
                  key={idx}
                  severity={rec.includes('🔴') ? 'error' : rec.includes('🟡') ? 'warning' : 'info'}
                  sx={{ mb: 1, mt: 1 }}
                >
                  {rec.replace(/[🔴🟡🟢💡📥🔧⚠️]/g, '').trim()}
                </Alert>
              ))}
            </Paper>
          )}

          {/* Error Categories */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              ⚠️ Error Categories
            </Typography>
            <List>
              {Object.entries(data.database.error_categories)
                .sort((a, b) => b[1] - a[1])
                .map(([category, count]) => (
                  <ListItem key={category} divider>
                    <ListItemText
                      primary={category}
                      secondary={`${count.toLocaleString()} failures`}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={count.toLocaleString()}
                        color={count > 100 ? 'error' : count > 50 ? 'warning' : 'default'}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          </Paper>

          {/* Task Failures */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🔴 Task Failures
            </Typography>
            {Object.entries(data.results_files.task_failures).length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell align="right">Failures</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(data.results_files.task_failures)
                      .sort((a, b) => b[1] - a[1])
                      .map(([task, count]) => (
                        <TableRow key={task}>
                          <TableCell>{task}</TableCell>
                          <TableCell align="right">
                            <Chip label={count} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No task failures found in results.json files
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
